import { loadTemplateJson, setReloadOn } from './TemplateJsonSlice.js';
import templateSelectedRootStore from './TemplateSelectedRootStore.js';
import templateSelectedItemStore from './TemplateSelectedItemStore.js';
import { selectRoot } from './TemplateSelectedRootSlice.js';
import { selectItem } from './TemplateSelectedItemSlice.js';
import { findElementById } from './TemplateJsonSlice.js';

// イベントと属性のストア・アクションをインポート
import eventItemStore from './EventItemStore.js';
import { loadEventItemData } from './EventItemSlice.js';
import attributeItemStore from './AttributeItemStore.js';
import { loadAttributeItemData } from './AttributeItemSlice.js';

class TemplateHistoryManager {
    constructor() {
        this.past = [];
        this.future = [];
        this.maxHistory = 50; // 保持する履歴の最大数
        this.isRestoring = false;

        // 連続入力のデバウンス用
        this.lastActionTime = 0;
        this.debounceTime = 800;
        this.lastActionType = null;

        // AIの一括処理を1つの履歴にまとめるためのフラグ
        this.isBatching = false;

        // Undo/Redoのチャタリング（連打・重複実行）防止用
        this.lastUndoTime = 0;
        this.lastRedoTime = 0;
        this.actionInterval = 200; // 連続実行を許容しないインターバル(ms)
    }

    /**
     * バッチ処理（AIエージェントの処理など）の開始
     * 開始直前の状態を1つの履歴として保存し、以降の個別アクションの記録を停止します。
     */
    startBatch(store) {
        if (this.isBatching) return;
        // 強制的に現在の状態を記録
        const stateCopy = this.createStateCopy(store);
        this.pushToPast(stateCopy, 'BATCH_START');
        this.isBatching = true;
    }

    /**
     * バッチ処理の終了
     * 記録を再開します。
     */
    endBatch(store) {
        this.isBatching = false;
    }

    /**
     * 現在の状態を文字列化して返す（選択状態は比較から除外する）
     * アクション適用前後の状態変化を検知するために使用します。
     */
    _getStateString(store) {
        const currentState = store.getState().templateJson;
        // 選択状態(_selection)は除外し、純粋なDOM・イベント・属性データのみで比較する
        return JSON.stringify({
            t: currentState.templates,
            tb: currentState.trashbox,
            e: eventItemStore.getState().eventItems.items,
            a: attributeItemStore.getState().attributeItems.items
        });
    }

    /**
     * 指定された状態オブジェクトを文字列化して返す
     */
    _serializeState(stateObj) {
        return JSON.stringify({
            t: stateObj.templates,
            tb: stateObj.trashbox,
            e: stateObj.events,
            a: stateObj.attributes
        });
    }

    /**
     * 現在のアプリケーション状態をキャプチャし、ディープコピーを作成します。
     */
    createStateCopy(store) {
        const currentState = store.getState().templateJson;
        const selectedRootId = templateSelectedRootStore.getState().templateSelectedRoot.targetId;
        const selectedItemId = templateSelectedItemStore.getState().templateSelectedItem.id;

        return {
            templates: JSON.parse(JSON.stringify(currentState.templates)),
            trashbox: JSON.parse(JSON.stringify(currentState.trashbox)),
            events: JSON.parse(JSON.stringify(eventItemStore.getState().eventItems.items)),
            attributes: JSON.parse(JSON.stringify(attributeItemStore.getState().attributeItems.items)),
            _selection: {
                rootId: selectedRootId,
                itemId: selectedItemId
            }
        };
    }

    /**
     * 状態コピーを履歴スタック(past)に保存し、やり直しスタック(future)をクリアします。
     */
    pushToPast(stateCopy, actionType) {
        if (this.isRestoring) return;
        if (this.isBatching) return;

        const now = Date.now();

        // 連続入力のデバウンス処理
        const isContinuousInput =
            (actionType === 'templateJson/updateElementAttribute' || actionType === 'templateJson/updateElementStyle') &&
            this.lastActionType === actionType &&
            (now - this.lastActionTime < this.debounceTime);

        this.lastActionTime = now;
        this.lastActionType = actionType;

        if (isContinuousInput && this.past.length > 0) {
            // 連続入力中は、最初の変更前の状態を維持するため、途中の状態は履歴に追加せずスキップ
            return;
        }

        this.past.push(stateCopy);
        if (this.past.length > this.maxHistory) {
            this.past.shift();
        }

        // 新規のアクションが実行されたため、やり直し履歴(future)はリセットする
        this.future = [];
    }

    /**
     * 状態の復元と、それに伴う選択状態の完全同期を行う内部メソッド
     */
    _applyStateAndSelection(store, targetState) {
        this.isRestoring = true;

        // 1. DOMツリー構造を復元
        store.dispatch(loadTemplateJson(targetState));

        // 2. イベントと属性の復元
        if (targetState.events) {
            eventItemStore.dispatch(loadEventItemData(targetState.events));
        }
        if (targetState.attributes) {
            attributeItemStore.dispatch(loadAttributeItemData(targetState.attributes));
        }

        // 3. 選択状態の同期
        const selection = targetState._selection || {};
        const state = store.getState().templateJson;

        // ツリーの先頭（アプリケーション）をデフォルトのフォールバック先にする
        const appId = (state.templates && state.templates.length > 0) ? state.templates[0].id : null;

        // ルート(ページ)の復元
        if (selection.rootId && findElementById(state, selection.rootId)) {
            templateSelectedRootStore.dispatch(selectRoot(selection.rootId));
        } else if (appId) {
            templateSelectedRootStore.dispatch(selectRoot(appId));
        }

        // アイテム(要素)の復元
        if (selection.itemId && findElementById(state, selection.itemId)) {
            templateSelectedItemStore.dispatch(selectItem(selection.itemId));
        } else if (appId) {
            templateSelectedItemStore.dispatch(selectItem(appId));
        }

        // 4. 強制再描画
        store.dispatch(setReloadOn());

        // スクロール等のUI追従
        const finalRootId = templateSelectedRootStore.getState().templateSelectedRoot.targetId;
        const finalItemId = templateSelectedItemStore.getState().templateSelectedItem.id;

        if (finalRootId && finalItemId) {
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('to-element-and-select', {
                    detail: JSON.stringify({
                        topTemplateId: finalRootId,
                        targetTemplateId: finalItemId,
                        expandNode: true
                    }),
                    bubbles: true,
                    composed: true,
                }));
            }, 50);
        }

        this.isRestoring = false;
    }

    undo(store) {
        if (this.past.length === 0) return;

        // 短時間での重複実行（チャタリング）をブロック
        const now = Date.now();
        if (now - this.lastUndoTime < this.actionInterval) return;
        this.lastUndoTime = now;

        const currentState = store.getState().templateJson;
        const currentRootId = templateSelectedRootStore.getState().templateSelectedRoot.targetId;
        const currentItemId = templateSelectedItemStore.getState().templateSelectedItem.id;

        // 現在の状態をfutureに退避
        this.future.push({
            templates: JSON.parse(JSON.stringify(currentState.templates)),
            trashbox: JSON.parse(JSON.stringify(currentState.trashbox)),
            events: JSON.parse(JSON.stringify(eventItemStore.getState().eventItems.items)),
            attributes: JSON.parse(JSON.stringify(attributeItemStore.getState().attributeItems.items)),
            _selection: {
                rootId: currentRootId,
                itemId: currentItemId
            }
        });

        // 過去の状態を取り出して適用
        const previousState = this.past.pop();
        if (!previousState) return;

        this._applyStateAndSelection(store, previousState);
    }

    redo(store) {
        if (this.future.length === 0) return;

        // 短時間での重複実行（チャタリング）をブロック
        const now = Date.now();
        if (now - this.lastRedoTime < this.actionInterval) return;
        this.lastRedoTime = now;

        const currentState = store.getState().templateJson;
        const currentRootId = templateSelectedRootStore.getState().templateSelectedRoot.targetId;
        const currentItemId = templateSelectedItemStore.getState().templateSelectedItem.id;

        // 現在の状態をpastに退避
        this.past.push({
            templates: JSON.parse(JSON.stringify(currentState.templates)),
            trashbox: JSON.parse(JSON.stringify(currentState.trashbox)),
            events: JSON.parse(JSON.stringify(eventItemStore.getState().eventItems.items)),
            attributes: JSON.parse(JSON.stringify(attributeItemStore.getState().attributeItems.items)),
            _selection: {
                rootId: currentRootId,
                itemId: currentItemId
            }
        });

        // 将来の状態を取り出して適用
        const nextState = this.future.pop();
        if (!nextState) return;

        this._applyStateAndSelection(store, nextState);
    }
}

export const templateHistoryManager = new TemplateHistoryManager();